import { useState, useEffect, useMemo, type CSSProperties } from "react";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useChangeTableOwner } from "../../hooks/use-node-access";
import { useAuth } from "../../../lib/hooks/use-auth";
import { NodeAvatar, showToast } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import type { LibraryNode } from "../../../lib/types";

export interface ChangeOwnerModalProps {
  open: boolean;
  onClose: () => void;
  entityTypeSlug: string;
  entityTypeName: string;
  currentOwnerAgentId: string | undefined;
}

export function ChangeOwnerModal({
  open,
  onClose,
  entityTypeSlug,
  entityTypeName,
  currentOwnerAgentId,
}: ChangeOwnerModalProps) {
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const changeOwner = useChangeTableOwner();

  const agents = useMemo(
    () => (tree.data ?? []).filter((n: LibraryNode) => n.type === "agent"),
    [tree.data],
  );

  const [selected, setSelected] = useState<string | null>(currentOwnerAgentId ?? null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(currentOwnerAgentId ?? null);
      setBusy(false);
    }
  }, [open, currentOwnerAgentId]);

  if (!open) return null;

  async function handleSave() {
    if (selected === (currentOwnerAgentId ?? null)) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await changeOwner.mutateAsync({
        entity_type_slug: entityTypeSlug,
        owner_agent_id: selected,
      });
      const label = selected
        ? agents.find((a) => a.id === selected)?.name ?? "agent"
        : "workspace";
      showToast(`Owner of ${entityTypeName} changed to ${label}`);
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't change owner", "error", true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div style={modal} role="dialog" aria-label="Change owner">
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 500, marginBottom: 6, color: "var(--color-text-primary)" }}>
          <i className="ti ti-user-edit" aria-hidden="true" />
          Change owner of {entityTypeName}
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 14 }}>
          Pick which agent owns this table, or clear ownership to make it workspace-shared.
        </div>

        <div style={{ overflowY: "auto", flex: 1, border: "var(--kuvio-border-default)", borderRadius: "var(--border-radius-md)" }}>
          <AgentRow
            name="No specific owner"
            sublabel="Workspace-shared table"
            selected={selected === null}
            onSelect={() => setSelected(null)}
          />
          {agents.map((agent) => (
            <AgentRow
              key={agent.id}
              name={agent.name}
              agent={agent}
              selected={selected === agent.id}
              onSelect={() => setSelected(agent.id)}
            />
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            style={{
              fontSize: 13,
              padding: "8px 14px",
              borderRadius: "var(--border-radius-md)",
              border: "none",
              fontWeight: 500,
              fontFamily: "var(--font-sans)",
              cursor: busy ? "default" : "pointer",
              background: "var(--c-blue-600, var(--color-accent, #2563eb))",
              color: "#fff",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Saving…" : "Change owner"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AgentRow({
  name,
  sublabel,
  agent,
  selected,
  onSelect,
}: {
  name: string;
  sublabel?: string;
  agent?: LibraryNode;
  selected: boolean;
  onSelect: () => void;
}) {
  const persona = agent
    ? getAgentPersona({ path: agent.path, agent_config: agent.agent_config as Record<string, unknown> | null })
    : undefined;

  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        fontSize: 13,
        cursor: "pointer",
        background: selected ? "var(--c-blue-50, var(--color-background-secondary))" : "transparent",
        borderBottom: "0.5px solid var(--color-border-secondary, transparent)",
      }}
    >
      <input type="radio" checked={selected} onChange={onSelect} />
      {agent ? (
        <NodeAvatar type="agent" persona={persona} size="xs" name={name} />
      ) : (
        <i className="ti ti-world" style={{ fontSize: 16, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
      )}
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}
        </span>
        {sublabel && (
          <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{sublabel}</span>
        )}
      </span>
    </label>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  fontFamily: "var(--font-sans)",
};

const modal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  padding: 24,
  width: 460,
  maxWidth: "92vw",
  maxHeight: "82vh",
  display: "flex",
  flexDirection: "column",
};
